import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Plus } from 'lucide-react';
import ManagementStaffModal from '../components/ManagementStaffModal';
import ZoomableImage from '../components/ZoomableImage';
import toast from 'react-hot-toast';
import ViewManagementStaffModal from '../components/ViewManagementStaffModal';
import { useReferenceStore, ReferenceStore, ReferenceOption } from '../store/useReferenceStore';
import SearchableSelect from '../components/SearchableSelect';

const PLACEHOLDER_IMG = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

// --- Icons ---
const TickIcon = () => <svg width="15" height="15" fill="none" stroke="#16a34a" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>;
const CrossIcon = () => <svg width="15" height="15" fill="none" stroke="#ef4444" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>;
const TC = ({ v }: { v: boolean }) => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
    {v ? <TickIcon /> : <CrossIcon />}
  </div>
);

// --- Global Compact Table Styles ---
const globalCss = `
  .ctbl th, .ctbl td { padding: 8px 10px !important; font-size: 0.78rem !important; border: 1px solid #c5cbd3 !important; vertical-align: middle !important; white-space: nowrap; }
  .ctbl th { padding: 10px 10px !important; font-size: 0.78rem !important; text-transform: uppercase; letter-spacing: 0.04em; color: #1a3a5c; font-weight: 700; background: #86afd8 !important; text-align: center; }
  .ctbl thead { background: #86afd8 !important; }
  .ctbl { table-layout: fixed; width: 100% !important; }
  /* Opt-out of the table-wide nowrap. Applies to th as well as td, so a
     two-word header can take a second line instead of overflowing its column
     on a narrow screen. */
  .ctbl .wc { white-space: normal !important; }
  /* Inline-editable cells: look like plain text until hovered/focused, so the
     table still reads as a table rather than a wall of form controls. */
  .ctbl .icell { width: 100%; border: 1px solid transparent; background: transparent; border-radius: 4px;
                 font: inherit; color: inherit; text-align: center; padding: 3px 4px; outline: none; cursor: pointer; }
  .ctbl .icell:hover { border-color: #cbd5e1; background: #f8fafc; }
  .ctbl .icell:focus { border-color: #2563eb; background: #fff; cursor: text; box-shadow: 0 0 0 2px rgba(37,99,235,0.12); }
  /* Placeholder is the empty-value dash, so it should read as an absent value
     rather than as hint text shouting for attention. */
  .ctbl .icell::placeholder { color: #94a3b8; opacity: 1; }
  .ctbl .icell.saving { opacity: 0.5; pointer-events: none; }
`;

const ManagementStaffTab = () => {
  const [staffList, setStaffList] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hoverRowId, setHoverRowId] = useState<number | null>(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [editingStaff, setEditingStaff] = useState<any | null>(null);
  const [viewingStaff, setViewingStaff] = useState<any | null>(null);

  // Inline editing of Class / Pay Scale straight from the table.
  // `draft` holds the cell currently being typed into, along with the value it
  // held on focus — so a blur can tell "changed" from "clicked through", and a
  // failed save has something to roll back to.
  const [draft, setDraft] = useState<{ id: number; field: string; value: string; original: any } | null>(null);
  const [savingCell, setSavingCell] = useState<string | null>(null);
  // The same reference lists the Register/Edit form's dropdowns use, so values
  // added in either place show up in the other without a second source of truth.
  // Removing an entry from a list is still only offered on the form, where
  // there's room for the affordance and less chance of a stray click.
  const refOptions = useReferenceStore((s: ReferenceStore) => s.options);
  const addReferenceOption = useReferenceStore((s: ReferenceStore) => s.addOption);
  const optionsFor = (category: string): ReferenceOption[] => refOptions[category] || [];

  const fetchStaff = async () => {
    setIsLoading(true);
    try {
      const API_URL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      const token = localStorage.getItem('accessToken');
      const res = await axios.get(`${API_URL}/api/management-staff/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStaffList(res.data);
    } catch (err) {
      toast.error("Failed to load management staff");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchStaff(); }, []);

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to remove ${name}? This cannot be undone.`)) return;
    try {
      const API_URL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API_URL}/api/management-staff/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success("Staff member removed successfully");
      fetchStaff();
    } catch (err) {
      toast.error("Failed to remove staff member");
    }
  };

  /**
   * PATCH a single field for one staff member. The endpoint takes multipart
   * form-data with a `staff_data` JSON blob and setattr()s whatever keys arrive,
   * so sending just the one field leaves everything else untouched — no need to
   * round-trip the whole record the way the edit modal does.
   *
   * Updates the row optimistically and rolls back on failure, so a dropped
   * request can't leave the table showing a value the database never took.
   */
  const saveField = async (staffId: number, field: string, value: any, original: any) => {
    if (String(original ?? '') === String(value ?? '')) return;   // untouched — don't spend a request

    const cellKey = `${staffId}:${field}`;
    setSavingCell(cellKey);
    setStaffList(prev => prev.map(s => (s.id === staffId ? { ...s, [field]: value } : s)));

    try {
      const API_URL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      const token = localStorage.getItem('accessToken');
      const fd = new FormData();
      fd.append('staff_data', JSON.stringify({ [field]: value }));
      await axios.patch(`${API_URL}/api/management-staff/${staffId}`, fd, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      toast.success('Saved');
    } catch (err: any) {
      setStaffList(prev => prev.map(s => (s.id === staffId ? { ...s, [field]: original } : s)));
      toast.error(
        err?.response?.status === 401
          ? 'Session expired — please log in again.'
          : 'Failed to save',
      );
    } finally {
      setSavingCell(null);
    }
  };

  /**
   * An inline-editable dropdown cell backed by a reference category. Shared by
   * Department / Designation / Class so the three columns can't drift apart.
   *
   * Type to filter, or type something new and add it to the reference list from
   * here — the same list the Register/Edit form's dropdowns read, so a value
   * added from the table shows up there (and on every other row) immediately.
   * Adding is an explicit click on the "Add …" row (or Enter), never a
   * side-effect of typing, so a half-typed word can't mint its own department.
   *
   * The list is portaled to <body> by SearchableSelect — the table sits in an
   * overflow:auto wrapper that would otherwise clip it.
   */
  const renderSelectCell = (staff: any, field: string, category: string, title: string) => {
    const current = staff[field] || '';
    const opts = optionsFor(category);
    // Options are keyed by label because that's what the staff record stores.
    // A value saved before it was removed from the list (or typed straight into
    // the DB) is appended so the cell still shows it instead of reading blank.
    const items = opts.map(o => ({ id: o.label, name: o.label }));
    if (current && !opts.some(o => o.label === current)) items.push({ id: current, name: current });

    return (
      <SearchableSelect
        options={items}
        value={current}
        onChange={(v) => saveField(staff.id, field, v || null, staff[field])}
        onAddNew={async (name) => {
          await addReferenceOption(category, name);
          // Selecting it is left to SearchableSelect, which calls onChange with
          // this id — so adding and assigning are one action from the table.
          return { id: name, name };
        }}
        variant="inline"
        inputClassName={`icell${savingCell === `${staff.id}:${field}` ? ' saving' : ''}`}
        allowClear
        addNewLabel={title.toLowerCase()}
        placeholder="—"
        title={`${title} — type to search or add`}
      />
    );
  };

  /**
   * Move one staff member to a new Sl.No, shifting everyone else around them.
   *
   * Sends the complete ordered id list rather than one position, so the write is
   * atomic server-side and two rows can never claim the same slot. Reorders
   * optimistically and rolls back on failure.
   */
  const moveToPosition = async (staffId: number, targetSlNo: number) => {
    const from = staffList.findIndex(s => s.id === staffId);
    if (from === -1) return;
    // Sl.No is 1-based and clamped: typing 99 in a 6-row table means "last".
    const to = Math.min(Math.max(targetSlNo, 1), staffList.length) - 1;
    if (to === from) return;

    const previous = staffList;
    const reordered = [...staffList];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setStaffList(reordered);
    setSavingCell(`${staffId}:sl_no`);

    try {
      const API_URL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      const token = localStorage.getItem('accessToken');
      await axios.put(`${API_URL}/api/management-staff/order`,
        { ordered_ids: reordered.map(s => s.id) },
        { headers: { 'Authorization': `Bearer ${token}` } },
      );
      toast.success('Order updated');
    } catch (err: any) {
      setStaffList(previous);
      toast.error(
        err?.response?.status === 401
          ? 'Session expired — please log in again.'
          : 'Failed to update order',
      );
    } finally {
      setSavingCell(null);
    }
  };

  /** Stacked qualification list — same shape the nurse list uses. */
  const renderQualifications = (qs: any[]) => {
    if (!qs?.length) return <span>-</span>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        {qs.map((q, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
            <span style={{ fontWeight: 600 }}>{q.qualification_name}</span>
            {q.institution_name && (
              <span style={{ color: '#64748b', fontSize: '0.67rem' }}>{q.institution_name}</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  const handleViewClick = (staff: any) => {
    setViewingStaff(staff);
  };

  const handleEditClick = (staff: any) => {
    setEditingStaff(staff);
    setIsModalOpen(true);
  };

  const handleOpenNewModal = () => {
    setEditingStaff(null);
    setIsModalOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'Active': return { bg: '#dcfce7', text: '#166534' };
      case 'On Leave': return { bg: '#ffedd5', text: '#c2410c' };
      case 'SUSPENDED': return { bg: '#fee2e2', text: '#b91c1c' };
      case 'Resigned': return { bg: '#fee2e2', text: '#b91c1c' };
      default: return { bg: '#f1f5f9', text: '#475569' };
    }
  };

  const getAgeAndSex = (dob: string, gender: string) => {
    let ageStr = '';
    if (dob) {
      const n = Math.abs(new Date(Date.now() - new Date(dob).getTime()).getUTCFullYear() - 1970);
      if (n > 0) ageStr = n.toString();
    }
    const g = (gender || '').toLowerCase();
    const sexStr = g.startsWith('f') ? 'Female' : g.startsWith('m') ? 'Male' : '';
    if (!ageStr && !sexStr) return 'N/A';
    return `${ageStr || '-'}/${sexStr || '-'}`;
  };

  const hasDL = (staffUrl: string | null | undefined) => {
    return !!staffUrl; // Returns true if a URL exists, false if null
  };

  const filteredStaff = staffList.filter(staff => 
    staff.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    (staff.emp_id && staff.emp_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (staff.department && staff.department.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div>
      <style>{globalCss}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', position: 'relative', width: '300px' }}>
          <Search size={18} color="#94a3b8" style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)' }} />
          <input 
            type="text" 
            placeholder="Search by Name or Dept..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '0.75rem 1rem 0.75rem 2.5rem', borderRadius: '0.5rem', border: '1px solid #cbd5e1', outline: 'none' }}
          />
        </div>
        
        <button onClick={handleOpenNewModal} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: '#0f172a', color: 'white', padding: '0.65rem 1.25rem', borderRadius: '7px', border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
          <Plus size={16} /> Register Staff
        </button>
      </div>

      <div style={{ overflowX: 'auto', backgroundColor: 'white', borderRadius: '9px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
        <table className="ctbl" style={{ width: '100%', borderCollapse: 'collapse', color: '#1d2631' }}>
          <thead>
            <tr>
              <th style={{ width: '40px' }}>Sl.No</th>
              <th style={{ width: '60px' }}>Photo</th>
              <th style={{ width: '120px', textAlign: 'left' }}>Name</th>
              <th style={{ width: '50px' }}>Age/Sex</th>
              <th style={{ width: '85px' }}>Department</th>
              <th style={{ width: '85px' }}>Designation</th>
              {/* Class holds a dropdown whose labels are free text ("Class 4 —
                  Senior Manager"), so it needs the width. Pay Scale is a
                  5-6 digit number and Transport is a single tick/cross icon —
                  the space comes from those two, leaving the row total
                  unchanged. Transport's header is abbreviated to match the
                  narrower column (and the neighbouring "DL"). */}
              <th style={{ width: '122px' }}>Class</th>
              <th className="wc" style={{ width: '64px' }}>Pay Scale</th>
              <th style={{ width: '118px' }}>Qualification</th>
              <th style={{ width: '54px' }} title="Transport contribution">Transp.</th>
              <th style={{ width: '40px' }}>DL</th>
              <th style={{ width: '70px' }}>Status</th>
              <th style={{ width: '54px' }}>View</th>
              <th style={{ width: '54px' }}>Edit</th>
              <th style={{ width: '54px' }}>Delete</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
                <tr><td colSpan={15} style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading staff data...</td></tr>
            ) : filteredStaff.length === 0 ? (
                <tr><td colSpan={15} style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>No staff found.</td></tr>
            ) : filteredStaff.map((staff, index) => {
              const colors = getStatusColor(staff.staff_status);
              return (
                <tr 
                  key={staff.id} 
                  style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: hoverRowId === staff.id ? '#f8fafc' : 'white', transition: 'background 0.1s' }}
                  onMouseEnter={() => setHoverRowId(staff.id)}
                  onMouseLeave={() => setHoverRowId(null)}
                >
                  {/* Sl.No doubles as the row-order control: type a new number
                      and the row moves there. Disabled while a search is active,
                      because the number shown would be the position within the
                      filtered view, not the real one. */}
                  <td style={{ textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>
                    {searchTerm ? (index + 1) : (
                      <input
                        type="number"
                        min="1"
                        max={staffList.length}
                        step="1"
                        className={`icell${savingCell === `${staff.id}:sl_no` ? ' saving' : ''}`}
                        style={{ color: '#94a3b8', fontWeight: 600 }}
                        value={
                          draft && draft.id === staff.id && draft.field === 'sl_no'
                            ? draft.value
                            : index + 1
                        }
                        onFocus={() => setDraft({ id: staff.id, field: 'sl_no', value: String(index + 1), original: index + 1 })}
                        onChange={(e) => setDraft(d => (d ? { ...d, value: e.target.value } : d))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                          if (e.key === 'Escape') { setDraft(null); (e.target as HTMLInputElement).blur(); }
                        }}
                        onBlur={() => {
                          if (!draft || draft.id !== staff.id || draft.field !== 'sl_no') return;
                          const next = Number(draft.value);
                          setDraft(null);
                          if (!draft.value || Number.isNaN(next)) return;
                          moveToPosition(staff.id, next);
                        }}
                        title="Sl.No — type a new number to move this row"
                      />
                    )}
                  </td>
                  
                  <td style={{ textAlign: 'center' }}>
                    <ZoomableImage
                      src={staff.photo_url || PLACEHOLDER_IMG}
                      alt="Staff Photo"
                      style={{ width: '45px', height: '45px', borderRadius: '5px', objectFit: 'cover', border: '1px solid #e2e8f0', display: 'block', margin: '0 auto' }}
                      fallbackSrc={PLACEHOLDER_IMG}
                    />
                  </td>
                  
                  <td className="wc" style={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>
                    {staff.full_name}
                  </td>

                  <td style={{ textAlign: 'center' }}>{getAgeAndSex(staff.dob, staff.gender)}</td>
                  <td style={{ textAlign: 'center' }}>
                    {renderSelectCell(staff, 'department', 'department', 'Department')}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {renderSelectCell(staff, 'designation', 'designation', 'Designation')}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {renderSelectCell(staff, 'class_level', 'management_level', 'Class')}
                  </td>

                  {/* Pay Scale — saves on blur or Enter rather than per keystroke. */}
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      className={`icell${savingCell === `${staff.id}:monthly_base_salary` ? ' saving' : ''}`}
                      placeholder="—"
                      value={
                        draft && draft.id === staff.id && draft.field === 'monthly_base_salary'
                          ? draft.value
                          : (staff.monthly_base_salary ?? '')
                      }
                      onFocus={() => setDraft({
                        id: staff.id,
                        field: 'monthly_base_salary',
                        value: String(staff.monthly_base_salary ?? ''),
                        original: staff.monthly_base_salary ?? null,
                      })}
                      onChange={(e) => setDraft(d => (d ? { ...d, value: e.target.value } : d))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        if (e.key === 'Escape') { setDraft(null); (e.target as HTMLInputElement).blur(); }
                      }}
                      onBlur={() => {
                        if (!draft || draft.id !== staff.id || draft.field !== 'monthly_base_salary') return;
                        const next = draft.value === '' ? null : Number(draft.value);
                        const original = draft.original;
                        setDraft(null);
                        if (next !== null && Number.isNaN(next)) return;
                        saveField(staff.id, 'monthly_base_salary', next, original);
                      }}
                      title="Monthly base salary (₹)"
                    />
                  </td>

                  <td className="wc" style={{ textAlign: 'center' }}>
                    {renderQualifications(staff.qualifications)}
                  </td>

                  <td style={{ textAlign: 'center' }}>
                    <TC v={staff.transport_contribution?.toLowerCase() === 'yes'} />
                  </td>
                  
                  <td style={{ textAlign: 'center' }}>
                    <TC v={hasDL(staff.driving_license_url)} />
                  </td>
                  
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ backgroundColor: colors.bg, color: colors.text, display: 'inline-flex', alignItems: 'center', padding: '2px 6px', borderRadius: '999px', fontSize: '0.65rem', fontWeight: 700, lineHeight: 1 }}>
                      {staff.staff_status}
                    </span>
                  </td>
                  
                  <td style={{ textAlign: 'center' }}>
                    <button onClick={() => handleViewClick(staff)} style={{ background: 'linear-gradient(135deg,#6366f1 0%,#4f46e5 100%)', color: 'white', border: 'none', borderRadius: '6px', padding: '0.35rem 0.7rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', display: 'inline-block', transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(79,70,229,0.2)' }}>View</button>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button onClick={() => handleEditClick(staff)} style={{ background: 'linear-gradient(135deg,#0ea5e9 0%,#2563eb 100%)', color: 'white', border: 'none', borderRadius: '6px', padding: '0.35rem 0.7rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', display: 'inline-block', transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(37,99,235,0.2)' }}>Edit</button>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button onClick={() => handleDelete(staff.id, staff.full_name)} style={{ background: 'linear-gradient(135deg,#f43f5e 0%,#e11d48 100%)', color: 'white', border: 'none', borderRadius: '6px', padding: '0.35rem 0.7rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', display: 'inline-block', transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(225,29,72,0.2)' }}>Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ViewManagementStaffModal 
        isOpen={!!viewingStaff} 
        onClose={() => setViewingStaff(null)} 
        staff={viewingStaff} 
      />
      <ManagementStaffModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onSuccess={fetchStaff}
        editData={editingStaff} 
      />
    </div>
  );
};

export default ManagementStaffTab;